import { useEffect, useState } from 'react';

import { faPlus, faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/videa-components/src/index';

import { SecurityAnswerField } from './SecurityAnswerField';

const OTHER_QUESTION_VALUE = '__OTHER__';

interface SecurityQuestionsSectionProps {
  payerId?: number;
  isSaving?: boolean;
  onSaveQuestions?: (
    payerId: number,
    questions: Array<{ question: string; answer: string }>,
  ) => Promise<void>;
}

const useSecurityQuestions = () => {
  const { t } = useLingui();
  return [
    t`What is your mother's maiden name?`,
    t`What city were you born in?`,
    t`What was the name of your first pet?`,
    t`What was the name of the street you grew up on?`,
    t`What was the name of your first school?`,
    t`What is your favorite food?`,
    t`What was the model of your first car?`,
    t`What was your favorite teacher's name?`,
    t`Who was your childhood best friend?`,
    t`What was your high school graduation year?`,
  ];
};

export const SecurityQuestionsSection = ({
  payerId,
  isSaving = false,
  onSaveQuestions,
}: SecurityQuestionsSectionProps) => {
  const securityQuestions = useSecurityQuestions();
  const { t } = useLingui();

  const [editingField, setEditingField] = useState<string | null>(null);
  const [isCopySuccess, setIsCopySuccess] = useState<string | null>(null);
  const [visibleQuestionCount, setVisibleQuestionCount] = useState<number>(0);

  // Selected security questions from the dropdowns
  // These store the actual question text that the user selected (or "None" if no question selected)
  const [securityQuestion1, setSecurityQuestion1] = useState<string>('');
  const [securityQuestion2, setSecurityQuestion2] = useState<string>('');
  const [securityQuestion3, setSecurityQuestion3] = useState<string>('');

  // Custom question text when "Other" is selected
  const [customQuestion1, setCustomQuestion1] = useState<string>('');
  const [customQuestion2, setCustomQuestion2] = useState<string>('');
  const [customQuestion3, setCustomQuestion3] = useState<string>('');

  // Error state for custom question fields (shown when "Other" is selected but field is empty)
  const [hasCustomQuestionError1, setHasCustomQuestionError1] =
    useState<boolean>(false);
  const [hasCustomQuestionError2, setHasCustomQuestionError2] =
    useState<boolean>(false);
  const [hasCustomQuestionError3, setHasCustomQuestionError3] =
    useState<boolean>(false);

  // Saved answer values - these are the committed/displayed answers
  // These are shown when the field is not being edited
  const [securityAnswer1, setSecurityAnswer1] = useState<string>('Videa');
  const [securityAnswer2, setSecurityAnswer2] = useState<string>('Videa');
  const [securityAnswer3, setSecurityAnswer3] = useState<string>('Videa');

  // Temporary answer values - used while the user is editing
  // When editing starts, we copy from the saved value to temp
  // When user saves, we copy from temp to saved value
  // When user cancels, we reset temp back to saved value
  // This pattern allows us to cancel edits without losing the original value
  const [tempSecurityAnswer1, setTempSecurityAnswer1] =
    useState<string>('Videa');
  const [tempSecurityAnswer2, setTempSecurityAnswer2] =
    useState<string>('Videa');
  const [tempSecurityAnswer3, setTempSecurityAnswer3] =
    useState<string>('Videa');

  // Reset all state when payerId changes to prevent data from persisting across payers
  useEffect(() => {
    setSecurityQuestion1('');
    setSecurityQuestion2('');
    setSecurityQuestion3('');
    setCustomQuestion1('');
    setCustomQuestion2('');
    setCustomQuestion3('');
    setHasCustomQuestionError1(false);
    setHasCustomQuestionError2(false);
    setHasCustomQuestionError3(false);
    setSecurityAnswer1('Videa');
    setSecurityAnswer2('Videa');
    setSecurityAnswer3('Videa');
    setTempSecurityAnswer1('Videa');
    setTempSecurityAnswer2('Videa');
    setTempSecurityAnswer3('Videa');
    setEditingField(null);
    setIsCopySuccess(null);
    setVisibleQuestionCount(0);
  }, [payerId]);

  // Helper to get the actual question text (handles "Other" option)
  const getQuestionText = (
    questionNumber: 1 | 2 | 3,
    overrideValue?: string,
  ): string => {
    const selectedValue =
      overrideValue ??
      (questionNumber === 1
        ? securityQuestion1
        : questionNumber === 2
          ? securityQuestion2
          : securityQuestion3);

    if (selectedValue === OTHER_QUESTION_VALUE) {
      return questionNumber === 1
        ? customQuestion1
        : questionNumber === 2
          ? customQuestion2
          : customQuestion3;
    }

    return selectedValue;
  };

  // Helper to get the current answer value for a question
  const getAnswerValue = (
    questionNumber: 1 | 2 | 3,
    overrideValue?: string,
  ): string => {
    if (overrideValue !== undefined) return overrideValue;

    if (questionNumber === 1) {
      return editingField === 'securityAnswer1'
        ? tempSecurityAnswer1
        : securityAnswer1;
    } else if (questionNumber === 2) {
      return editingField === 'securityAnswer2'
        ? tempSecurityAnswer2
        : securityAnswer2;
    } else {
      return editingField === 'securityAnswer3'
        ? tempSecurityAnswer3
        : securityAnswer3;
    }
  };

  // Build and save the questions array
  const saveSecurityQuestions = async (overrides?: {
    question1?: string;
    question2?: string;
    question3?: string;
    answer1?: string;
    answer2?: string;
    answer3?: string;
  }) => {
    if (!payerId || !onSaveQuestions) return;

    // Get the actual question text (handles "Other" option)
    const q1 = getQuestionText(1, overrides?.question1);
    const q2 = getQuestionText(2, overrides?.question2);
    const q3 = getQuestionText(3, overrides?.question3);

    // Build questions array with only valid questions
    const questions: Array<{ question: string; answer: string }> = [];

    if (q1 && q1 !== 'None' && q1 !== OTHER_QUESTION_VALUE) {
      questions.push({
        question: q1,
        answer: getAnswerValue(1, overrides?.answer1),
      });
    }

    if (q2 && q2 !== 'None' && q2 !== OTHER_QUESTION_VALUE) {
      questions.push({
        question: q2,
        answer: getAnswerValue(2, overrides?.answer2),
      });
    }

    if (q3 && q3 !== 'None' && q3 !== OTHER_QUESTION_VALUE) {
      questions.push({
        question: q3,
        answer: getAnswerValue(3, overrides?.answer3),
      });
    }

    // Always send the request, even if questions array is empty
    try {
      await onSaveQuestions(payerId, questions);
    } catch (error) {
      console.error('Failed to save security questions:', error);
      throw error;
    }
  };

  const handleStartEdit = (fieldId: string) => {
    // Ensure only one field is edited at a time
    if (editingField && editingField !== fieldId) {
      handleCancelEdit();
    }
    setEditingField(fieldId);
    if (fieldId === 'securityAnswer1') {
      setTempSecurityAnswer1(securityAnswer1);
    } else if (fieldId === 'securityAnswer2') {
      setTempSecurityAnswer2(securityAnswer2);
    } else if (fieldId === 'securityAnswer3') {
      setTempSecurityAnswer3(securityAnswer3);
    }
  };

  const handleCancelEdit = () => {
    const currentField = editingField;
    setEditingField(null);
    if (currentField === 'securityAnswer1') {
      setTempSecurityAnswer1(securityAnswer1);
    } else if (currentField === 'securityAnswer2') {
      setTempSecurityAnswer2(securityAnswer2);
    } else if (currentField === 'securityAnswer3') {
      setTempSecurityAnswer3(securityAnswer3);
    }
  };

  const handleSaveSecurityAnswer1 = async () => {
    const answerToSave = tempSecurityAnswer1.trim() || 'Videa';
    setSecurityAnswer1(answerToSave);
    setTempSecurityAnswer1(answerToSave);
    setEditingField(null);
    await saveSecurityQuestions({ answer1: answerToSave });
  };

  const handleSaveSecurityAnswer2 = async () => {
    const answerToSave = tempSecurityAnswer2.trim() || 'Videa';
    setSecurityAnswer2(answerToSave);
    setTempSecurityAnswer2(answerToSave);
    setEditingField(null);
    await saveSecurityQuestions({ answer2: answerToSave });
  };

  const handleSaveSecurityAnswer3 = async () => {
    const answerToSave = tempSecurityAnswer3.trim() || 'Videa';
    setSecurityAnswer3(answerToSave);
    setTempSecurityAnswer3(answerToSave);
    setEditingField(null);
    await saveSecurityQuestions({ answer3: answerToSave });
  };

  const handleCopy = async (value: string, fieldId: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setIsCopySuccess(fieldId);
      setTimeout(() => setIsCopySuccess(null), 2000);
    } catch (err) {
      console.error(`Failed to copy ${fieldId}:`, err);
    }
  };

  const handleAddQuestion = async () => {
    if (visibleQuestionCount < 3) {
      const newCount = visibleQuestionCount + 1;
      setVisibleQuestionCount(newCount);

      // Auto-select the first question and set default answer
      const firstQuestion = securityQuestions[0];
      const defaultAnswer = 'Videa';

      // Set the question and answer values directly without entering edit mode
      if (newCount === 1) {
        setSecurityQuestion1(firstQuestion);
        setSecurityAnswer1(defaultAnswer);
        setTempSecurityAnswer1(defaultAnswer);
      } else if (newCount === 2) {
        setSecurityQuestion2(firstQuestion);
        setSecurityAnswer2(defaultAnswer);
        setTempSecurityAnswer2(defaultAnswer);
      } else if (newCount === 3) {
        setSecurityQuestion3(firstQuestion);
        setSecurityAnswer3(defaultAnswer);
        setTempSecurityAnswer3(defaultAnswer);
      }

      // Immediately save the newly added question to the API
      const overrides: Record<string, string> = {};
      if (newCount === 1) {
        overrides.question1 = firstQuestion;
        overrides.answer1 = defaultAnswer;
      } else if (newCount === 2) {
        overrides.question2 = firstQuestion;
        overrides.answer2 = defaultAnswer;
      } else if (newCount === 3) {
        overrides.question3 = firstQuestion;
        overrides.answer3 = defaultAnswer;
      }

      await saveSecurityQuestions(overrides);
    }
  };

  const handleRemoveQuestion = async () => {
    if (visibleQuestionCount > 0) {
      const questionToRemove = visibleQuestionCount;

      // Clear the data for the question being removed
      if (questionToRemove === 1) {
        setSecurityQuestion1('');
        setCustomQuestion1('');
        setHasCustomQuestionError1(false);
        setSecurityAnswer1('Videa');
        setTempSecurityAnswer1('Videa');
        if (editingField === 'securityAnswer1') {
          setEditingField(null);
        }
      } else if (questionToRemove === 2) {
        setSecurityQuestion2('');
        setCustomQuestion2('');
        setHasCustomQuestionError2(false);
        setSecurityAnswer2('Videa');
        setTempSecurityAnswer2('Videa');
        if (editingField === 'securityAnswer2') {
          setEditingField(null);
        }
      } else if (questionToRemove === 3) {
        setSecurityQuestion3('');
        setCustomQuestion3('');
        setHasCustomQuestionError3(false);
        setSecurityAnswer3('Videa');
        setTempSecurityAnswer3('Videa');
        if (editingField === 'securityAnswer3') {
          setEditingField(null);
        }
      }

      setVisibleQuestionCount(visibleQuestionCount - 1);

      // Save the updated questions (with the removed question cleared)
      const overrides: Record<string, string> = {};
      if (questionToRemove === 1) overrides.question1 = '';
      if (questionToRemove === 2) overrides.question2 = '';
      if (questionToRemove === 3) overrides.question3 = '';

      await saveSecurityQuestions(overrides);
    }
  };

  const handleQuestion1Change = async (value: string) => {
    setSecurityQuestion1(value);
    // Clear error when changing selection
    setHasCustomQuestionError1(false);

    // Don't save immediately if "Other" is selected - wait for custom question input
    if (value === OTHER_QUESTION_VALUE) {
      handleStartEdit('securityAnswer1');
      return;
    }

    if (value && value !== 'None') {
      handleStartEdit('securityAnswer1');
      await saveSecurityQuestions({
        question1: value,
        answer1: securityAnswer1,
      });
    } else {
      if (editingField === 'securityAnswer1') {
        handleCancelEdit();
      }
      await saveSecurityQuestions({ question1: value });
    }
  };

  const handleQuestion2Change = async (value: string) => {
    setSecurityQuestion2(value);
    // Clear error when changing selection
    setHasCustomQuestionError2(false);

    // Don't save immediately if "Other" is selected - wait for custom question input
    if (value === OTHER_QUESTION_VALUE) {
      handleStartEdit('securityAnswer2');
      return;
    }

    if (value && value !== 'None') {
      handleStartEdit('securityAnswer2');
      await saveSecurityQuestions({
        question2: value,
        answer2: securityAnswer2,
      });
    } else {
      if (editingField === 'securityAnswer2') {
        handleCancelEdit();
      }
      await saveSecurityQuestions({ question2: value });
    }
  };

  const handleQuestion3Change = async (value: string) => {
    setSecurityQuestion3(value);
    // Clear error when changing selection
    setHasCustomQuestionError3(false);

    // Don't save immediately if "Other" is selected - wait for custom question input
    if (value === OTHER_QUESTION_VALUE) {
      handleStartEdit('securityAnswer3');
      return;
    }

    if (value && value !== 'None') {
      handleStartEdit('securityAnswer3');
      await saveSecurityQuestions({
        question3: value,
        answer3: securityAnswer3,
      });
    } else {
      if (editingField === 'securityAnswer3') {
        handleCancelEdit();
      }
      await saveSecurityQuestions({ question3: value });
    }
  };

  const handleCustomQuestionChange = (
    questionNumber: 1 | 2 | 3,
    value: string,
  ) => {
    // Clear error when user starts typing
    if (questionNumber === 1) {
      setCustomQuestion1(value);
      if (value.trim()) setHasCustomQuestionError1(false);
    } else if (questionNumber === 2) {
      setCustomQuestion2(value);
      if (value.trim()) setHasCustomQuestionError2(false);
    } else {
      setCustomQuestion3(value);
      if (value.trim()) setHasCustomQuestionError3(false);
    }
  };

  const handleCustomQuestionBlur = async (questionNumber: 1 | 2 | 3) => {
    const customValue =
      questionNumber === 1
        ? customQuestion1
        : questionNumber === 2
          ? customQuestion2
          : customQuestion3;

    const setError =
      questionNumber === 1
        ? setHasCustomQuestionError1
        : questionNumber === 2
          ? setHasCustomQuestionError2
          : setHasCustomQuestionError3;

    // If empty, show error and don't save
    if (!customValue.trim()) {
      setError(true);
      return;
    }

    // Clear error and save
    setError(false);
    await saveSecurityQuestions();
  };

  const renderQuestion = (
    questionNumber: 1 | 2 | 3,
    questionValue: string,
    onQuestionChange: (value: string) => Promise<void>,
    customQuestionValue: string,
    hasCustomQuestionError: boolean,
    answerValue: string,
    tempAnswerValue: string,
    setTempAnswerValue: (value: string) => void,
    onSave: () => Promise<void>,
    isLastQuestion: boolean,
  ) => {
    const fieldId = `securityAnswer${questionNumber}` as const;
    const questionId = `securityQuestion${questionNumber}`;
    const isOtherSelected = questionValue === OTHER_QUESTION_VALUE;

    return (
      <div key={questionNumber}>
        <div className="vai-grid vai-grid-cols-2 vai-gap-4">
          <div className="vai-group">
            <Label htmlFor={questionId}>
              <Trans>Question</Trans> {questionNumber}
            </Label>
            <Select value={questionValue} onValueChange={onQuestionChange}>
              <SelectTrigger id={questionId}>
                <SelectValue placeholder={t`Select a question`} />
              </SelectTrigger>
              <SelectContent>
                {securityQuestions.map((question: string) => (
                  <SelectItem key={question} value={question}>
                    {question}
                  </SelectItem>
                ))}
                <SelectItem value={OTHER_QUESTION_VALUE}>
                  <Trans>Other (enter custom question)</Trans>
                </SelectItem>
              </SelectContent>
            </Select>
            {isOtherSelected && (
              <>
                <Input
                  className={`vai-mt-2 ${
                    hasCustomQuestionError
                      ? 'vai-border-red-500 focus-visible:vai-ring-red-500'
                      : ''
                  }`}
                  placeholder={t`Enter your custom question`}
                  value={customQuestionValue}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleCustomQuestionChange(questionNumber, e.target.value)
                  }
                  onBlur={() => handleCustomQuestionBlur(questionNumber)}
                />
                {hasCustomQuestionError && (
                  <p className="vai-text-sm vai-text-red-500 vai-mt-1">
                    <Trans>Custom question is required</Trans>
                  </p>
                )}
              </>
            )}
          </div>
          <SecurityAnswerField
            fieldId={fieldId}
            questionNumber={questionNumber}
            questionValue={
              isOtherSelected ? customQuestionValue : questionValue
            }
            answerValue={answerValue}
            tempAnswerValue={tempAnswerValue}
            editingField={editingField}
            isSaving={isSaving}
            isCopySuccess={isCopySuccess}
            onStartEdit={handleStartEdit}
            onCancelEdit={handleCancelEdit}
            onSave={onSave}
            onCopy={handleCopy}
            onTempAnswerChange={setTempAnswerValue}
          />
        </div>
        {isLastQuestion && (
          <div className="vai-mt-2 vai-flex vai-gap-4">
            {questionNumber < 3 && (
              <Button
                variant="link"
                size="sm"
                className="!vai-p-0 vai-text-blue-600 vai-flex vai-items-center vai-gap-1"
                onClick={handleAddQuestion}
              >
                <FontAwesomeIcon icon={faPlus} className="vai-w-3 vai-h-3" />
                <Trans>Add another question</Trans>
              </Button>
            )}
            <Button
              variant="link"
              size="sm"
              className="!vai-p-0 vai-text-red-600 vai-flex vai-items-center vai-gap-1"
              onClick={handleRemoveQuestion}
            >
              <FontAwesomeIcon icon={faXmark} className="vai-w-3 vai-h-3" />
              <Trans>Remove question</Trans>
            </Button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <Label>
        <Trans>Security Questions (sometimes required)</Trans>
      </Label>
      <p className="vai-mt-2 vai-text-sm vai-text-muted-foreground">
        <Trans>
          If you selected security questions in the payer portal, add them here.
        </Trans>
      </p>
      <div className="vai-space-y-4 vai-mt-2">
        {/* Add question button - visible when no questions are shown */}
        {visibleQuestionCount === 0 && (
          <div>
            <Button
              variant="link"
              size="sm"
              className="!vai-p-0 vai-text-blue-600 vai-flex vai-items-center vai-gap-1"
              onClick={handleAddQuestion}
            >
              <FontAwesomeIcon icon={faPlus} className="vai-w-3 vai-h-3" />
              <Trans>Add question</Trans>
            </Button>
          </div>
        )}

        {/* Question 1 - Visible if count >= 1 */}
        {visibleQuestionCount >= 1 &&
          renderQuestion(
            1,
            securityQuestion1,
            handleQuestion1Change,
            customQuestion1,
            hasCustomQuestionError1,
            securityAnswer1,
            tempSecurityAnswer1,
            setTempSecurityAnswer1,
            handleSaveSecurityAnswer1,
            visibleQuestionCount === 1,
          )}

        {/* Question 2 - Visible if count >= 2 */}
        {visibleQuestionCount >= 2 &&
          renderQuestion(
            2,
            securityQuestion2,
            handleQuestion2Change,
            customQuestion2,
            hasCustomQuestionError2,
            securityAnswer2,
            tempSecurityAnswer2,
            setTempSecurityAnswer2,
            handleSaveSecurityAnswer2,
            visibleQuestionCount === 2,
          )}

        {/* Question 3 - Visible if count >= 3 */}
        {visibleQuestionCount >= 3 &&
          renderQuestion(
            3,
            securityQuestion3,
            handleQuestion3Change,
            customQuestion3,
            hasCustomQuestionError3,
            securityAnswer3,
            tempSecurityAnswer3,
            setTempSecurityAnswer3,
            handleSaveSecurityAnswer3,
            visibleQuestionCount === 3,
          )}
      </div>
    </div>
  );
};
